import { defineComponent, onMounted, reactive, SetupContext, ref, computed } from '@vue/composition-api';
import vueRouter from '@/router/index';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import { AttachmentFile } from '@/shared/type';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    publishDetail: string;                                                  // 掲載内容
    publicationRange: string;                                               // 公開範囲
    title: string;                                                          // タイトル
    publishStartDate: string;                                               // 掲載日
    sender: string;                                                         // 発信者
    attachmentFiles: AttachmentFile[];                                      // 添付ファイル
    isAttachDisplay: boolean;                                               // 添付ファイル表示切替
};

/**
 * お知らせ詳細画面コンポーネント
 */
export default defineComponent({
    components: { RichTextEditor, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            publishDetail: '',                                              // 掲載内容
            publicationRange: '',                                           // 公開範囲
            title: '',                                                      // タイトル
            publishStartDate: '',                                           // 掲載日
            sender: '',                                                     // 発信者
            attachmentFiles: [],                                            // 添付ファイル情報
            isAttachDisplay: false,                                         // 添付ファイル表示切替
        });
        const commonRef = ref<any>(null);                                   // メッセージダイアログ
        const infoId = computed(() => ctx.root.$route.params.id).value;     // お知らせ詳細ID
        const fileIconList = SystemConst.FILE_EXTENSION_ICON;               // 添付ファイル拡張子アイコンリスト
        const detailHeight = SystemConst.DETAIL_HEIGHT.INFORMATION;

        /**
         * 掲載内容の設定
         * （リッチテキストエディタに表示するため、DOMの生成後のライフサイクルであるonMountedを使用）
         */
        onMounted(() => {
            // 初期表示データの取得関数呼び出し
            getDetail();
        });

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-document-outline');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', 'お知らせ詳細');
        }

        /**
         * 初期表示データの取得
         */
        async function getDetail() {
            // お知らせ詳細IDをパラメータとして詳細データを取得する
            const response = await ctx.root.$store.dispatch('information/getInformationDetail',
                {
                    informationId: infoId
                }
            );

            // API通信のレスポンスが正常に取得できなかった場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.NOT_FOUND || response.status === HttpStatus.BAD_REQUEST) {
                    // 400, 404エラーの場合、エラーメッセージを表示して画面に遷移する

                    // お知らせ一覧画面の利用権限の有無で遷移先を変更する
                    if (ctx.root.$store.state.authenticationUser.role.isInformation) {
                        // ログインユーザが「お知らせ」ロールに入っている場合、一覧画面に遷移する
                        commonRef.value.displayErrorMessageTransitionDialog(response, '/information/list');
                        return;
                    } else {
                        // ログインユーザが「お知らせ」ロールに入っていない場合、ホーム画面に遷移する
                        commonRef.value.displayErrorMessageTransitionDialog(response, '/home');
                        return;
                    }
                }
                // その他のエラーの場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // API通信のレスポンスが正常に取得できた場合、表示するデータを設定する
            state.publicationRange = response.data.informationDetail.publicationRange;
            state.title = response.data.informationDetail.title;
            state.publishStartDate = response.data.informationDetail.publishStartDate;
            state.sender = response.data.informationDetail.sender;
            // リッチテキストエディタに取得した内容を登録する
            state.publishDetail = response.data.informationDetail.body;

            // 添付ファイルデータを取得した場合、以下を添付ファイルの数分繰り返す
            const attachmentFilesNumber = response.data.informationDetail.attachmentFiles.length;
            if (attachmentFilesNumber !== 0) {
                for (let i = 0; i <= attachmentFilesNumber - 1; i++) {
                    const attachFile: AttachmentFile[] = [                  // 一時保存する添付ファイル情報
                        {
                            fileIcon: '',
                            fileSequence: 0,
                            fileName: '',
                            fileSize: ''
                        }
                    ];
                    attachFile[0].fileSequence = response.data.informationDetail.attachmentFiles[i].fileSequence;
                    attachFile[0].fileName = response.data.informationDetail.attachmentFiles[i].fileName;

                    // 添付ファイル名から拡張子部分を取得する
                    const extension = attachFile[0].fileName.substring(attachFile[0].fileName.lastIndexOf('.') + 1);
                    // アイコンのリストを作成する
                    const fileIcon = Object.keys(fileIconList);
                    // 添付ファイルアイコンを拡張子から判別し、設定する
                    Object.values(fileIconList).forEach((element, index) => {
                        // 添付ファイルの拡張子を小文字に変換して比較する
                        const iconList = element.some((extensionList) => extensionList === extension.toLowerCase());
                        if (iconList === true) {
                            // 拡張子を見つけたときの添え字番号で検索し、設定する
                            attachFile[0].fileIcon = fileIcon[index];
                        }
                        if (attachFile[0].fileIcon === '' && fileIcon.length - 1 === index) {
                            // 最後まで検索し、一致する拡張子がなかった場合
                            attachFile[0].fileIcon = fileIcon[index];
                        }
                    });

                    // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                    const fileSize = String(Math.round((response.data.informationDetail.attachmentFiles[i].fileSize / 1024) * 1000) / 1000);
                    attachFile[0].fileSize = fileSize;

                    state.attachmentFiles.push(attachFile[0]);
                }
                // 添付ファイルの表示切り替える
                state.isAttachDisplay = true;
            }
        }

        /**
         * 添付ファイルダウンロード処理
         * @param fileSequence ファイルシーケンス
         * @param fileName ダウンロードファイル名
         */
        async function fileDownload(fileSequence: number, fileName: string) {
            // 添付ファイルを取得するAPIを呼び出す
            // パラメータ：infoId, fileSequence
            const response = await  ctx.root.$store.dispatch('information/attachmentDownload',
                {
                    informationId: infoId,
                    fileSequence: fileSequence,
                    fileName: fileName
                });

            // API通信成否判定
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
            }
            return;
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            router.back();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            commonRef,
            detailHeight,
            fileDownload,
            back,
            onResize
        };
    }
});
